// ════════════════════════════════════════════════════════════════════
// src/pages/Equipe.js — o admin monta o time
// ════════════════════════════════════════════════════════════════════
// Fluxo, em três passos, sem backend novo:
//   1. o admin cadastra o e-mail da pessoa e escolhe o papel  → convites/{email}
//   2. a pessoa se cadastra no painel com AQUELE e-mail
//   3. no primeiro login ela vê o convite, aceita, e vira     → vinculos/{uid}
//
// Não existe envio de e-mail aqui — o painel não tem servidor de e-mail.
// Por isso a tela entrega o texto pronto para o admin mandar no WhatsApp.
// ════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import { useAuth, chaveEmail } from '../context/AuthContext';
import { db } from '../services/firebase';
import { collection, query, where, getDocs, doc, setDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { PAPEIS, labelPapel, corPapel, AREAS } from '../permissoes';
import { registrar } from '../services/auditoria';

const ehEmailValido = (e) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(e || '').trim());
const fmtData = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d) ? '' : d.toLocaleDateString('pt-BR');
};

export default function Equipe() {
  const { user, contaUid, papel, ehDono } = useAuth();

  const [membros, setMembros]   = useState([]);
  const [convites, setConvites] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [erro, setErro]         = useState('');

  // formulário de convite
  const [email, setEmail]       = useState('');
  const [novoPapel, setNovoPapel] = useState('vendas');
  const [salvando, setSalvando] = useState(false);
  const [recado, setRecado]     = useState('');

  const [editando, setEditando] = useState(null);  // membro em edição
  const [copiado, setCopiado]   = useState('');

  const carregar = useCallback(async () => {
    if (!contaUid) return;
    setLoading(true); setErro('');
    try {
      const [vs, cs] = await Promise.all([
        getDocs(query(collection(db, 'vinculos'), where('donoUid', '==', contaUid))),
        getDocs(query(collection(db, 'convites'), where('donoUid', '==', contaUid))),
      ]);
      setMembros(vs.docs.map(d => ({ uid: d.id, ...d.data() })));
      setConvites(cs.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      setErro(/permission/i.test(e?.message || '')
        ? 'As regras do Firestore ainda não liberam "vinculos" e "convites". Publique as regras novas e recarregue.'
        : ('Não consegui carregar a equipe: ' + (e?.message || e)));
    } finally { setLoading(false); }
  }, [contaUid]);

  useEffect(() => { carregar(); }, [carregar]);

  // ── convidar ──
  const convidar = async () => {
    const chave = chaveEmail(email);
    if (!ehEmailValido(chave)) { setRecado('⚠ Digite um e-mail válido.'); return; }
    if (chave === chaveEmail(user?.email)) { setRecado('⚠ Esse é o seu próprio e-mail.'); return; }
    if (membros.some(m => chaveEmail(m.email) === chave)) {
      setRecado('⚠ Essa pessoa já está na equipe. Troque o papel dela na lista abaixo.'); return;
    }
    setSalvando(true); setRecado('');
    try {
      await setDoc(doc(db, 'convites', chave), {
        email: chave,
        donoUid: contaUid,
        donoEmail: user?.email || '',
        papel: novoPapel,
        criadoEm: new Date().toISOString(),
        criadoPor: user?.uid || '',
      });
      registrar(contaUid, { user, papel, acao: 'equipe.convidou', alvo: chave,
        detalhe: `convidou ${chave} como ${labelPapel(novoPapel)}` });
      setEmail('');
      setRecado(`✅ Convite criado para ${chave}. Agora mande o passo a passo para ela (botão "Copiar convite" abaixo).`);
      carregar();
    } catch (e) {
      setRecado('⚠ Não consegui criar o convite: ' + (e?.message || e));
    } finally { setSalvando(false); }
  };

  // ── editar papel ──
  const salvarPapel = async (m, papelNovo) => {
    try {
      await updateDoc(doc(db, 'vinculos', m.uid), { papel: papelNovo });
      registrar(contaUid, { user, papel, acao: 'equipe.trocouPapel', alvo: m.email,
        detalhe: `${m.email}: ${labelPapel(m.papel)} → ${labelPapel(papelNovo)}` });
      setEditando(null);
      carregar();
    } catch (e) { setErro('Não consegui salvar: ' + (e?.message || e)); }
  };

  // ── ver os números de toda a equipe ──
  // Desligado (o normal): a pessoa vê só os aparelhos dela e os da equipe
  // toda, em Configurações e no WhatsApp. Quem barra de verdade é o n8n.
  const alternarVerTodos = async (m) => {
    const novo = !m.verTodosNumeros;
    try {
      await updateDoc(doc(db, 'vinculos', m.uid), { verTodosNumeros: novo });
      registrar(contaUid, { user, papel, acao: novo ? 'equipe.liberouNumeros' : 'equipe.restringiuNumeros',
        alvo: m.email, detalhe: `${m.email}: ${novo ? 'vê' : 'não vê mais'} os números de toda a equipe` });
      carregar();
    } catch (e) { setErro('Não consegui salvar: ' + (e?.message || e)); }
  };

  // ── suspender / reativar ──
  const alternarAtivo = async (m) => {
    const novo = m.ativo === false;
    try {
      await updateDoc(doc(db, 'vinculos', m.uid), { ativo: novo });
      registrar(contaUid, { user, papel, acao: novo ? 'equipe.reativou' : 'equipe.suspendeu',
        alvo: m.email, detalhe: `${novo ? 'reativou' : 'suspendeu'} ${m.email}` });
      carregar();
    } catch (e) { setErro('Não consegui salvar: ' + (e?.message || e)); }
  };

  // ── remover ──
  const remover = async (m) => {
    if (!window.confirm(
      `Remover ${m.email} da equipe?\n\nEla perde o acesso na hora. O que ela já cadastrou continua na base.`
    )) return;
    try {
      await deleteDoc(doc(db, 'vinculos', m.uid));
      try { await deleteDoc(doc(db, 'convites', chaveEmail(m.email))); } catch (e) {}
      registrar(contaUid, { user, papel, acao: 'equipe.removeu', alvo: m.email,
        detalhe: `removeu ${m.email} (${labelPapel(m.papel)})` });
      carregar();
    } catch (e) { setErro('Não consegui remover: ' + (e?.message || e)); }
  };

  const cancelarConvite = async (c) => {
    if (!window.confirm(`Cancelar o convite de ${c.email}?`)) return;
    try {
      await deleteDoc(doc(db, 'convites', c.id));
      registrar(contaUid, { user, papel, acao: 'equipe.cancelouConvite', alvo: c.email,
        detalhe: `cancelou o convite de ${c.email}` });
      carregar();
    } catch (e) { setErro('Não consegui cancelar: ' + (e?.message || e)); }
  };

  const textoConvite = (c) =>
`Oi! Liberei seu acesso ao painel da FaLei como ${labelPapel(c.papel)}.

Pra entrar:
1. Abra ${typeof window !== 'undefined' ? window.location.origin : 'o painel'}
2. Clique em Entrar e depois em criar conta
3. Cadastre-se com ESTE e-mail: ${c.email}
4. Vai aparecer o convite na tela — é só aceitar

Qualquer dúvida me chama.`;

  const copiar = (c) => {
    navigator.clipboard.writeText(textoConvite(c));
    setCopiado(c.id);
    setTimeout(() => setCopiado(''), 2000);
  };

  // Só o dono da conta administra a equipe. Um admin convidado não existe
  // hoje, mas a checagem evita surpresa se um dia existir.
  if (!ehDono) {
    return (
      <div style={{ maxWidth: '760px', margin: '0 auto' }}>
        <div style={{ ...card, maxWidth: '440px', margin: '60px auto', textAlign: 'center' }}>
          <div style={{ fontSize: '34px', marginBottom: '12px' }}>🔒</div>
          <h2 style={{ fontFamily: 'Syne, sans-serif', fontSize: '18px', fontWeight: '800', marginBottom: '8px' }}>
            Só o dono da conta gerencia a equipe
          </h2>
          <p style={{ color: '#52526e', fontSize: '13px' }}>
            Fale com quem te convidou para mudar acessos.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '860px', margin: '0 auto' }}>
      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: '26px',
          fontWeight: '800', letterSpacing: '-0.5px', marginBottom: '6px' }}>Equipe</h1>
        <p style={{ color: '#52526e', fontSize: '13px' }}>
          Quem mais entra nesta conta, e o que cada um pode fazer.
        </p>
      </div>

      {erro && (
        <div style={{ ...aviso, background: 'rgba(239,68,68,0.07)', borderColor: 'rgba(239,68,68,0.22)', color: '#dc2626' }}>
          ⚠ {erro}
        </div>
      )}

      {/* Convidar */}
      <div style={{ ...card, marginBottom: '16px' }}>
        <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: '700', fontSize: '15px', marginBottom: '14px' }}>
          Convidar alguém
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 180px auto', gap: '10px', alignItems: 'end' }}>
          <div>
            <label style={lbl}>E-mail da pessoa</label>
            <input value={email} onChange={e => setEmail(e.target.value)} type="email"
              placeholder="vendedor@salao.com.br" style={inp}
              onKeyDown={e => e.key === 'Enter' && convidar()} />
          </div>
          <div>
            <label style={lbl}>Papel</label>
            <select value={novoPapel} onChange={e => setNovoPapel(e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
              {Object.entries(PAPEIS).filter(([k]) => k !== 'admin')
                .map(([k, p]) => <option key={k} value={k}>{p.label}</option>)}
            </select>
          </div>
          <button onClick={convidar} disabled={salvando}
            style={{ background: salvando ? '#9898b8' : 'linear-gradient(135deg, #6c47ff, #5535e0)',
              color: '#fff', border: 'none', borderRadius: '10px', padding: '11px 22px',
              fontSize: '14px', fontWeight: '600', cursor: salvando ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}>
            {salvando ? 'Criando...' : 'Convidar'}
          </button>
        </div>

        <div style={{ marginTop: '12px', fontSize: '12px', color: '#52526e', lineHeight: 1.6,
          background: '#f8f8fc', borderRadius: '9px', padding: '12px 14px' }}>
          <b style={{ color: corPapel(novoPapel) }}>{labelPapel(novoPapel)}:</b> {PAPEIS[novoPapel]?.desc}
          <div style={{ marginTop: '6px', fontSize: '11px', color: '#9898b8' }}>
            Telas: {(PAPEIS[novoPapel]?.areas || []).filter(a => a !== 'home').map(a => AREAS[a]).join(' · ')}
          </div>
        </div>

        {recado && (
          <div style={{ ...aviso, marginTop: '12px', marginBottom: 0,
            background: recado.startsWith('✅') ? 'rgba(16,185,129,0.07)' : 'rgba(239,68,68,0.07)',
            borderColor: recado.startsWith('✅') ? 'rgba(16,185,129,0.22)' : 'rgba(239,68,68,0.22)',
            color: recado.startsWith('✅') ? '#047857' : '#dc2626' }}>
            {recado}
          </div>
        )}
      </div>

      {/* Convites pendentes */}
      {convites.length > 0 && (
        <div style={{ ...card, marginBottom: '16px' }}>
          <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: '700', fontSize: '15px', marginBottom: '6px' }}>
            Convites aguardando
            <span style={{ fontFamily: 'monospace', fontSize: '12px', color: '#d97706', marginLeft: '8px' }}>
              {convites.length}
            </span>
          </div>
          <p style={{ fontSize: '12px', color: '#9898b8', marginBottom: '14px' }}>
            O painel não manda e-mail. Copie o passo a passo e mande para a pessoa.
          </p>

          {convites.map(c => (
            <div key={c.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: '10px', padding: '12px 14px', borderRadius: '10px', marginBottom: '8px',
              background: 'rgba(245,158,11,0.05)', border: '1px solid rgba(245,158,11,0.22)' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: '13px', fontWeight: '600', overflow: 'hidden',
                  textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.email}</div>
                <div style={{ fontSize: '11px', color: '#9898b8', fontFamily: 'monospace', marginTop: '2px' }}>
                  {labelPapel(c.papel)} · criado {fmtData(c.criadoEm)} · ainda não aceitou
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                <button onClick={() => copiar(c)} style={btnMini('#6c47ff')}>
                  {copiado === c.id ? '✓ Copiado' : '📋 Copiar convite'}
                </button>
                <button onClick={() => cancelarConvite(c)} style={btnMini('#dc2626')}>Cancelar</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Membros */}
      <div style={card}>
        <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: '700', fontSize: '15px', marginBottom: '14px' }}>
          Pessoas com acesso
          <span style={{ fontFamily: 'monospace', fontSize: '12px', color: '#6c47ff', marginLeft: '8px' }}>
            {membros.length + 1}
          </span>
        </div>

        {/* o dono */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 14px', borderRadius: '10px', marginBottom: '8px',
          background: '#f8f8fc', border: '1px solid rgba(0,0,0,0.07)' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '13px', fontWeight: '600' }}>
              {user?.email} <span style={{ color: '#9898b8', fontWeight: '400' }}>(você)</span>
            </div>
            <div style={{ fontSize: '11px', color: '#9898b8', fontFamily: 'monospace', marginTop: '2px' }}>
              dono da conta
            </div>
          </div>
          <span style={{ fontSize: '10px', fontWeight: '700', fontFamily: 'monospace',
            padding: '3px 9px', borderRadius: '5px',
            background: corPapel('admin') + '18', color: corPapel('admin') }}>ADMINISTRADOR</span>
        </div>

        {loading && <div style={{ fontSize: '13px', color: '#9898b8', padding: '12px 0' }}>Carregando...</div>}

        {!loading && membros.length === 0 && (
          <div style={{ fontSize: '13px', color: '#9898b8', padding: '14px 0', fontStyle: 'italic' }}>
            Ninguém mais tem acesso ainda. Convide acima.
          </div>
        )}

        {membros.map(m => {
          const suspenso = m.ativo === false;
          const emEdicao = editando === m.uid;
          return (
            <div key={m.uid} style={{ padding: '12px 14px', borderRadius: '10px', marginBottom: '8px',
              background: suspenso ? 'rgba(0,0,0,0.02)' : '#fff',
              border: '1px solid rgba(0,0,0,0.08)', opacity: suspenso ? 0.65 : 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: '600', overflow: 'hidden',
                    textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {m.nome || m.email}
                    {suspenso && <span style={{ color: '#d97706', fontWeight: '400', marginLeft: '6px' }}>· suspenso</span>}
                  </div>
                  <div style={{ fontSize: '11px', color: '#9898b8', fontFamily: 'monospace', marginTop: '2px' }}>
                    {m.email} · entrou em {fmtData(m.criadoEm)}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
                  {!emEdicao && (
                    <span style={{ fontSize: '10px', fontWeight: '700', fontFamily: 'monospace',
                      padding: '3px 9px', borderRadius: '5px',
                      background: corPapel(m.papel) + '18', color: corPapel(m.papel) }}>
                      {labelPapel(m.papel).toUpperCase()}
                    </span>
                  )}
                  <button onClick={() => setEditando(emEdicao ? null : m.uid)} style={btnMini('#6c47ff')}>
                    {emEdicao ? 'Fechar' : '✎ Editar'}
                  </button>
                </div>
              </div>

              {emEdicao && (
                <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid rgba(0,0,0,0.07)' }}>
                  <label style={lbl}>Papel</label>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
                    {Object.entries(PAPEIS).filter(([k]) => k !== 'admin').map(([k, p]) => (
                      <button key={k} onClick={() => salvarPapel(m, k)} style={{
                        padding: '8px 14px', borderRadius: '9px', fontSize: '12px', fontWeight: '600',
                        cursor: 'pointer', fontFamily: 'DM Sans, sans-serif',
                        border: '1.5px solid ' + (m.papel === k ? p.cor : 'rgba(0,0,0,0.1)'),
                        background: m.papel === k ? p.cor : '#fff',
                        color: m.papel === k ? '#fff' : '#52526e' }}>
                        {p.label}{m.papel === k ? ' ✓' : ''}
                      </button>
                    ))}
                  </div>
                  <div style={{ fontSize: '12px', color: '#52526e', marginBottom: '12px' }}>
                    {PAPEIS[m.papel]?.desc}
                  </div>
                  <label style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', fontSize: '12.5px',
                    color: '#1a1a2e', marginBottom: '12px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={!!m.verTodosNumeros} onChange={() => alternarVerTodos(m)} />
                    <span>
                      <b>Vê os números de toda a equipe</b> (WhatsApp e Configurações).
                      <span style={{ color: '#9898b8' }}> Desligado: vê só os aparelhos dela e os marcados como "equipe toda".</span>
                    </span>
                  </label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={() => alternarAtivo(m)} style={btnMini(suspenso ? '#059669' : '#d97706')}>
                      {suspenso ? '▶ Reativar acesso' : '⏸ Suspender acesso'}
                    </button>
                    <button onClick={() => remover(m)} style={btnMini('#dc2626')}>🗑 Remover da equipe</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ ...aviso, marginTop: '16px', background: 'rgba(108,71,255,0.05)',
        borderColor: 'rgba(108,71,255,0.2)', color: '#52526e' }}>
        <b style={{ color: '#6c47ff' }}>Como funciona:</b> quem você convida continua com o login dele, mas
        trabalha <b>dentro da sua conta</b> — mesmos clientes, mesmos leads, mesmos disparos. Remover
        alguém tira o acesso na hora; o que a pessoa cadastrou fica.
      </div>
    </div>
  );
}

const card = { background: '#fff', border: '1px solid rgba(0,0,0,0.08)',
  borderRadius: '14px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' };
const lbl = { fontSize: '11px', fontWeight: '700', color: '#52526e',
  textTransform: 'uppercase', letterSpacing: '0.06em',
  fontFamily: 'monospace', display: 'block', marginBottom: '6px' };
const inp = { width: '100%', padding: '10px 12px', borderRadius: '9px',
  border: '1.5px solid rgba(0,0,0,0.1)', fontSize: '14px', background: '#f8f8fc',
  outline: 'none', boxSizing: 'border-box', fontFamily: 'DM Sans, sans-serif', color: '#0a0a18' };
const aviso = { borderRadius: '10px', border: '1px solid', padding: '12px 16px',
  marginBottom: '14px', fontSize: '13px', lineHeight: 1.6 };
const btnMini = (cor) => ({ background: '#fff', color: cor, border: `1.5px solid ${cor}55`,
  borderRadius: '8px', padding: '6px 12px', fontSize: '12px', fontWeight: '600',
  cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', whiteSpace: 'nowrap' });
